import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LifeBuoy, Mail, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SUPPORT_EMAIL = "vend@thegoodbox.com";
const CATEGORIES = [
  { value: "general", label: "General question" },
  { value: "billing", label: "Billing & payments" },
  { value: "technical", label: "Technical issue" },
  { value: "kiosk", label: "Kiosk / hardware" },
  { value: "account", label: "Account & access" },
];

export function SupportModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const [category, setCategory] = useState("general");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const reset = () => { setCategory("general"); setSubject(""); setMessage(""); };

  const submit = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Please add a subject and a message"); return; }
    setSending(true);
    try {
      await api.post("/support", { subject: subject.trim(), message: message.trim(), category });
      toast.success("Support request sent — we'll be in touch shortly");
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your request");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg" data-testid="support-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5" style={{ color: "var(--brand-accent, #C026D3)" }} /> Contact Support
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-neutral-500">
            Send our team a message and we'll reply to <span className="font-medium text-neutral-700">{user?.email}</span>. Typical response within one business day.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="support-category">Topic</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="support-category" data-testid="support-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="support-subject">Subject</Label>
            <Input id="support-subject" data-testid="support-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary" maxLength={140} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="support-message">How can we help?</Label>
            <Textarea id="support-message" data-testid="support-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe your question or issue…" rows={5} />
          </div>

          <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-neutral-600">
            <Mail className="h-3.5 w-3.5" /> Prefer email? {SUPPORT_EMAIL}
          </a>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="support-cancel">Cancel</Button>
          <Button onClick={submit} disabled={sending} data-testid="support-submit">
            {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : "Send request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
